import type { ReactNode } from 'react'
import {
  CreditCardOutlined,
  GiftOutlined,
  HistoryOutlined,
  IdcardOutlined,
  SafetyOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import { PERM } from '@/shared/auth/partnerPermissions'

export interface NavItem {
  key: string
  path: string
  labelKey: string
  icon: ReactNode
  /**
   * Kamida bittasi bo'lsa havola ko'rinadi. Berilmasa — hammaga.
   *
   * ⚠️ Bu QULAYLIK, xavfsizlik EMAS (04-ROLES.md §8). Yashirilgan sahifa
   * URL orqali ochilishi mumkin — u yerda `RequirePermission` "ruxsat
   * yo'q" ko'rsatadi, ma'lumot esa baribir backend tekshiruvidan o'tadi.
   */
  permission?: readonly string[]
}

/**
 * «Sheriklar kabineti» navigatsiyasi — ADR-030 §2. BOR-YO'G'I TO'RT
 * BO'LIM va bu chegara ataylab: ro'yxat pastki tab-barga ham chiziladi
 * (`MobileTabBar`), u yerda esa beshtadan ortiq element tugmalarni
 * barmoq sig'maydigan darajada toraytiradi.
 *
 * ⚠️ MENYU SHERIK TURIGA QARAB O'ZI SHAKLLANADI — alohida shox YO'Q.
 * `PartnerKind` to'plamlari (ADR-029 §2) shuni beradi:
 *
 *   · agent    → Kassa, Amallarim, Balans   (`subscriber.read` yo'q,
 *                ya'ni «Abonentlar» O'ZIDAN yo'qoladi)
 *   · reseller → Abonentlar, Amallarim, Balans (`subscriber.directory`
 *                yo'q, ya'ni FAQAT kassa O'ZIDAN yo'qoladi)
 *   · «pul ham oladigan» reseller → TO'RTALASI, chunki `CASH_DESK`
 *     bayrog'i unga `subscriber.directory` ni qo'shadi
 *
 * ADR-030 aynan shu holatni «o'zidan ishlaydi» deb yozgan edi — bu
 * yerda hech qanday `if (kind === 'AGENT')` yo'q.
 *
 * <h2>⚠️ HAR BAND O'Z ENDPOINTINING HUQUQIGA BOG'LANADI (2026-08-21)</h2>
 * Ilgari «Mening amallarim» ham, «Berilgan paketlar» ham
 * `subscriber.directory` ostida edi — u esa KASSA belgisi, bu
 * ekranlarning huquqi emas. Oqibati Staging'da o'lchandi: `cash_desk`
 * o'chiq to'rtta resellerda (`reseller_obid`, `reseller_nurbek`,
 * `reseller1`, `reseller2`) panel bor-yo'g'i uchta bo'limni ko'rsatardi.
 *
 * Jonli o'lchov (kassasiz reseller, 2026-08-21) shartni aniqladi:
 *   · `GET /api/subscribers/directory` → **403** — Kassa ekrani
 *     qidiruv maydonisiz boshlanmaydi, ya'ni uning sharti O'ZI shu;
 *   · `GET /api/agents/{id}/operations` va `/commission-summary`
 *     → **200** (`agent.read`, ikkala turda ham bor).
 *
 * ⚠️ «Har doim BO'SH bo'lardi» degan eski izoh HAM O'LCHANDI va
 * NOTO'G'RI chiqdi: depoziti bor kassasiz resellerning ro'yxatida
 * `DEPOSIT` qatori turadi (7 500 000 AFN, `actor` bilan).
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    // ⚠️ Shart `subscriber.directory` da QOLDI va bu O'LCHANGAN qaror,
    // ko'chirib qolgan bayroq emas: ekran qidiruv maydonidan boshlanadi
    // va kassasiz sherikda o'sha so'rov 403 qaytaradi. `agent.fund` ga
    // ko'chirish ham AYNI natijani berardi (ikkovi `CASH_DESK` da
    // ajralmas juftlik), lekin u holda menyu bilan boshlang'ich ekran
    // (`StartScreen`) ikki xil kodga qarab qolardi.
    key: 'kassa',
    path: '/',
    labelKey: 'nav.kassa',
    icon: <ShoppingCartOutlined />,
    permission: [PERM.subscriberDirectory],
  },
  {
    key: 'subscribers',
    path: '/subscribers',
    labelKey: 'nav.subscribers',
    icon: <TeamOutlined />,
    permission: [PERM.subscriberRead],
  },
  {
    key: 'myOperations',
    path: '/my-operations',
    labelKey: 'nav.myOperations',
    icon: <HistoryOutlined />,
    permission: [PERM.agentRead],
  },
  { key: 'profile', path: '/profile', labelKey: 'nav.profile', icon: <WalletOutlined /> },
]

/**
 * IKKINCHI QATOR — jurnallar. Pastki tab-barga CHIQMAYDI.
 *
 * ⚠️ NEGA ALOHIDA RO'YXAT, «beshinchi band» EMAS. `MobileTabBar` ni
 * to'rtdan oshirish tugmalarni 44px dan toraytiradi
 * ({@link NAV_ITEMS} ustidagi izoh) va kassirning asosiy qurilmasi —
 * telefon. Shuning uchun bu ikkitasi:
 *   · kompyuterda — yuqori qatordagi menyuda, birinchi to'rttaning
 *     yonida (u yerda joy bor va egasi aynan shuni so'ragan);
 *   · telefonda — hisob menyusida (`ProfileMenu`), tab-bar tegilmaydi.
 * Ular hech qachon BIR VAQTDA ikki joyda chizilmaydi — `AppHeader` ning
 * «menyu takrorlanmaydi» qoidasi shu yerda ham kuchda.
 *
 * ⚠️ «Berilgan paketlar» ALOHIDA BO'LIM, «Mening amallarim» ning filtri
 * emas (egasi talabi, 2026-08-20): «bergan paketlarini ham ko'rish
 * kerak — shuni alohida menyu qilib chiqarib qo'yish kerak». Shu qaror
 * bilan birga `GRANT` amallar filtrining tanlagichidan olib tashlandi
 * (`OPERATION_FILTER_KINDS`).
 *
 * ⚠️ «Mening kirishlarim» HUQUQSIZ — u sherik hisobining O'Z ma'lumoti
 * va endpoint ham huquq so'ramaydi (`AuthResource#myLogins` javadoc'i),
 * identifikatorni TOKENDAN oladi.
 *
 * ⚠️ «Berilgan paketlar» sharti `agent.read` — u AYNI `/operations`
 * endpointidan `kind=GRANT` bilan o'qiydi. `grant.issue` (berish
 * huquqi) TALAB QILINMAYDI: ikkala sherik turida ham ikkovi birga
 * keladi, so'rovni esa o'qish huquqi ochadi — bandni berish huquqiga
 * bog'lash shartni ENDPOINTDAN uzib qo'yardi.
 */
export const SECONDARY_NAV_ITEMS: readonly NavItem[] = [
  /**
   * ⚠️ «Kartalarim» — egasi talabi (2026-08-24): sherik o'ziga
   * berilgan partiyalarni, ulardagi kodlarni va o'zi ishlatgan
   * kartalarni ko'rsin.
   *
   * ⛔ SHARTI `voucher.stock.read`, `voucher.activate` EMAS. Ikkinchisi
   * AMAL huquqi va IKKALA turda ham bor — u qo'yilsa band hammada
   * ko'rinardi va bosilganda 403/404 berardi. Kod esa bearer sir
   * (`partnerPermissions.ts` dagi to'liq sabab).
   *
   * ⚠️ Huquq serverda BOR — `PartnerKind.java:63` (AGENT) va `:125`
   * (RESELLER), o'lchandi 2026-08-24.
   *
   * ⚠️ IKKINCHI QATORDA: `NAV_ITEMS` to'rtta bo'lib qolishi kerak
   * (yuqoridagi izoh) — beshinchi tugma barmoq nishonini 44px dan
   * toraytirardi.
   */
  {
    key: 'myVouchers',
    path: '/vouchers',
    labelKey: 'nav.myVouchers',
    icon: <IdcardOutlined />,
    permission: [PERM.voucherStockRead],
  },
  {
    key: 'grants',
    path: '/granted',
    labelKey: 'nav.grants',
    icon: <GiftOutlined />,
    permission: [PERM.agentRead],
  },
]

/**
 * HISOB MENYUSI — profil dropdowni, HAR DOIM (telefonda ham,
 * kompyuterda ham). Egasi talabi (2026-08-25): «kirishlarim menyuni
 * user dropdownga olish kk».
 *
 * ⚠️ NEGA UCHINCHI RO'YXAT, `SECONDARY_NAV_ITEMS` NING BIR QISMI EMAS.
 * Ikkinchi ro'yxat qurilmaga qarab IKKI joyda chiziladi (kompyuterda
 * yuqori menyu, telefonda dropdown), bu esa HAR DOIM bitta joyda.
 * Ikkovini bitta ro'yxatda saqlash «menyu takrorlanmaydi» qoidasini
 * shart bilan buzardi: kompyuterda «Kirishlarim» yuqori qatorda ham,
 * dropdownda ham chiqib qolardi.
 *
 * ⚠️ HUQUQSIZ va bu ataylab — sherik hisobining O'Z ma'lumoti,
 * endpoint identifikatorni TOKENDAN oladi (`AuthResource#myLogins`).
 */
export const ACCOUNT_NAV_ITEMS: readonly NavItem[] = [
  { key: 'myLogins', path: '/my-logins', labelKey: 'nav.myLogins', icon: <SafetyOutlined /> },
]

/**
 * «Karta faollashtirish» — MENYUDA YO'Q, chunki u endi Kassa
 * ekranining bir bo'lagi (egasi talabi 2026-08-25: «uni vazifasi ham
 * shunaqa»).
 *
 * ⛔ LEKIN BAND BUTUNLAY O'CHIRILMADI va sabab o'lchangan:
 * `voucher.activate` IKKALA sherik turining BAZAVIY to'plamida
 * (`PartnerKind.java:55`, `:92`), Kassa ekrani esa `subscriber.directory`
 * ga bog'langan — u KASSA bayrog'i (`CASH_DESK`). Staging'da kassasiz
 * to'rtta reseller bor (`reseller_obid`, `reseller1`, `reseller2`,
 * `reseller_dev`). Amalni faqat Kassa ichiga qo'yish ularni server
 * ochib bergan huquqdan JIMGINA mahrum qilardi — 2026-08-21
 * nuqsonining aynan takrori.
 *
 * Shuning uchun band FAQAT Kassa ko'rinmaydigan sherikda chiqadi
 * (`useVisibleNav`), qolganlarida sahifa Kassa ichida.
 */
export const VOUCHER_FALLBACK_ITEM: NavItem = {
  key: 'voucher',
  path: '/voucher',
  labelKey: 'nav.voucher',
  icon: <CreditCardOutlined />,
  permission: [PERM.voucherActivate],
}

const ALL_NAV_ITEMS: readonly NavItem[] = [
  ...NAV_ITEMS,
  ...SECONDARY_NAV_ITEMS,
  ...ACCOUNT_NAV_ITEMS,
  VOUCHER_FALLBACK_ITEM,
]

/**
 * Joriy yo'lga mos elementni topadi — eng UZUN mos yo'l g'olib chiqadi.
 *
 * `/` hamma narsaning prefiksi, shuning uchun oddiy `startsWith` bilan
 * «Kassa» doim tanlangan bo'lib qolardi.
 *
 * ⚠️ IKKALA ro'yxat bo'yicha yuradi: aks holda `/granted` ochilganda
 * yuqoridagi menyuda «Kassa» yonib turardi.
 *
 * ⚠️ `/voucher` — `/vouchers` NING PREFIKSI (2026-08-24). «Eng uzun
 * g'olib» qoidasi buni O'ZIDAN to'g'ri hal qiladi: `/vouchers`
 * ochilganda ikkala band ham mos keladi, lekin uzunrog'i tanlanadi.
 * Oddiy «birinchi mos kelgani» bo'lsa «Kartalarim» sahifasida menyuda
 * «Karta faollashtirish» yonib turardi.
 */
export function findActiveNavKey(pathname: string): string {
  let best: NavItem | null = null
  for (const item of ALL_NAV_ITEMS) {
    const matches = item.path === '/' ? pathname === '/' : pathname.startsWith(item.path)
    if (matches && (!best || item.path.length > best.path.length)) {
      best = item
    }
  }
  return best?.key ?? 'kassa'
}
